/**
 * Get in touch section for website preview – full form UI, buttons disabled.
 * Uses same profile picture as hero (useArtistProfilePic) with fallback.
 */

import React, { useState } from "react";
import { User } from "lucide-react";
import OptimizedImage from "@/components/OptimizedImage";
import { Section } from "@/layout/Section";
import { useArtistProfilePic } from "@/api/hooks/useArtistDisplayName";
import type { Profile } from "@/types/types";

interface CommonGetInTouchMobileProps {
  profile?: Profile | null;
}

const OCCASION_OPTIONS = [
  "Virtual",
  "Video Shoutout",
  "Campus",
  "Corporate",
  "Private Party",
  "Concert/Festival",
  "Wedding",
  "Charity",
  "Fashion Show",
  "Exhibition",
];

export default function CommonGetInTouchMobile({ profile }: CommonGetInTouchMobileProps) {
  const profilePic = useArtistProfilePic();
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="flex flex-col sm:flex-row justify-center items-center w-screen gap-5">
      <div className="w-full w-[300px] md:w-1/2 flex flex-col">
        <Section>
          <div className="flex flex-col justify-start overflow-hidden">
            <div className="flex flex-col">
              <div className="w-screen sm:w-full md:w-full  mt-5 sm:mt-0">
                <div className="rounded-[0.9rem] lg:rounded-[4.9rem] w-full">
                  <div className="flex lg:flex-row flex-col gap-8">
                    <div className="flex flex-col justify-start items-start gap-20 w-full">
                      <div className="w-full px-5">
                        <div className="flex flex-row justify-between items-center gap-2">
                          <div className="flex flex-col">
                            <p className="text-[28px] sm:text-[38px] font-semibold text-white">
                              GET IN TOUCH
                            </p>
                            <p className="text-[14px] text-white/80">
                              Just a step away from booking your dream star
                            </p>
                          </div>
                          <div className="block sm:hidden w-[100px] h-[100px] relative overflow-hidden rounded-full bg-[#2C2C2E] flex items-center justify-center">
                            {profilePic ? (
                              <OptimizedImage
                                src={profilePic}
                                alt="profile-picture"
                                className="object-cover object-top rounded-full w-full h-full"
                                width={100}
                                height={100}
                              />
                            ) : (
                              <User className="w-10 h-10 text-[#8E8E93] opacity-50" />
                            )}
                          </div>
                        </div>

                        {/* Step progress */}
                        <div className="relative flex items-center justify-between text-base text-gray-600 dark:text-gray-400 mt-20  ">
                          <div className="flex items-center">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${activeStep === 0 || activeStep === 1
                                ? "bg-[#ED9670]/30 border-[0.5px] border-[#ED9670]"
                                : "bg-white text-white/20"
                                }`}
                            />
                            <span
                              className={`absolute -top-6 lg:-top-9 font-[300] min-w-[9rem] text-[12px] lg:text-[18px] ${activeStep === 0 || activeStep === 1 ? "text-white" : "text-inherit"
                                }`}
                            >
                              Event Details
                            </span>
                          </div>
                          <div className="relative w-full">
                            <div className="bg-white/20 w-full h-1" />
                            <div
                              className="absolute top-0 h-1 bg-gradient-to-r from-[#ECA16B] from-[30%] to-[#F83F98] transition-all duration-300"
                              style={{ width: activeStep >= 1 ? "100%" : "50%" }}
                            />
                          </div>
                          <div className="relative flex items-center">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${activeStep === 1
                                ? "bg-[#ED9670]/30 border-[0.5px] border-[#ED9670] text-white"
                                : "bg-white text-white/20"
                                }`}
                            />
                            <span
                              className={`absolute text-right -top-6 right-0 font-[300] min-w-[9rem] text-[12px] lg:text-[18px] ${activeStep === 1 ? "text-white" : "text-inherit"
                                }`}
                            >
                              Personal Details
                            </span>
                          </div>
                        </div>

                        {/* Step 0: Event Details */}
                        {activeStep === 0 && (
                          <div className="mt-14 flex flex-col gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-white mb-1">
                                  What&apos;s the Occasion
                                </label>
                                <select
                                  disabled
                                  className="w-full bg-transparent border border-white rounded-lg p-3 text-white/70 text-left appearance-none"
                                >
                                  <option value="">Select Occasion</option>
                                  {OCCASION_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm text-white mb-1">Event Date</label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    disabled
                                    placeholder="dd/mm/yyyy"
                                    className="w-full bg-transparent border border-white rounded-lg p-3 text-white/70 pr-10"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/70">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.5}
                                      stroke="currentColor"
                                      className="w-5 h-5"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                                      />
                                    </svg>
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-white mb-1">
                                  Specify your Budget
                                </label>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70">
                                    ₹
                                  </span>
                                  <input
                                    type="text"
                                    disabled
                                    placeholder="Enter your budget..."
                                    className="w-full bg-transparent border border-white rounded-lg p-3 pl-7 text-white/70"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm text-white mb-1">
                                  How many people will attend?
                                </label>
                                <input
                                  type="text"
                                  disabled
                                  placeholder="Number of attendees..."
                                  className="w-full bg-transparent border border-white rounded-lg p-3 text-white/70"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm text-white mb-1">Venue City</label>
                              <input
                                type="text"
                                disabled
                                placeholder="Type your City..."
                                className="w-full bg-transparent border border-white rounded-lg p-3 text-white/70"
                              />
                            </div>
                            <button
                              type="button"
                              disabled
                              className="w-full mt-6 py-4 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold uppercase cursor-not-allowed opacity-90 pointer-events-none"
                            >
                              Submit
                            </button>
                          </div>
                        )}

                        {/* Step 1: Personal Details */}
                        {activeStep === 1 && (
                          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-white/90 mb-1">Full Name</label>
                              <input
                                type="text"
                                disabled
                                placeholder="Enter full name"
                                className="w-full bg-black/50 border border-zinc-600 rounded-lg p-3 text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-white/90 mb-1">Phone</label>
                              <input
                                type="tel"
                                disabled
                                placeholder="Enter phone number"
                                className="w-full bg-black/50 border border-zinc-600 rounded-lg p-3 text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-white/90 mb-1">Email</label>
                              <input
                                type="email"
                                disabled
                                placeholder="Enter email"
                                className="w-full bg-black/50 border border-zinc-600 rounded-lg p-3 text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-white/90 mb-1">
                                Message (optional)
                              </label>
                              <textarea
                                disabled
                                rows={3}
                                placeholder="Enter your message"
                                className="w-full bg-black/50 border border-zinc-600 rounded-lg p-3 text-white resize-none"
                              />
                            </div>
                            <div className="flex gap-2 pt-4">
                              <button
                                type="button"
                                onClick={() => setActiveStep(0)}
                                className="px-4 py-2 rounded-lg border border-zinc-600 text-white"
                              >
                                Back
                              </button>
                              <button
                                type="button"
                                disabled
                                className="px-4 py-2 rounded-lg bg-pink-700 text-white cursor-not-allowed opacity-70"
                              >
                                Submit
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
      <div className="hidden md:block aspect-[10/16] h-0 md:h-[600px] relative overflow-hidden rounded-xl bg-[#2C2C2E] flex items-center justify-center">
        {profilePic ? (
          <OptimizedImage
            src={profilePic}
            alt="profile-picture"
            className="w-full h-full object-cover object-top"
            fill
          />
        ) : (
          <User className="w-24 h-24 text-[#8E8E93] opacity-50" />
        )}
      </div>
    </div>
  );
}
